import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TrendlineDataPoint } from '@/types/dashboard.types';
import { DateRange } from '@/types/dashboard.types';
import { getAggregationPeriod } from './analytics-card-helpers';
import { useAnalyticsWindow } from './analytics-window-context';

interface BarChartProps {
  trendline: TrendlineDataPoint[];
  // Top-left label, e.g. "Daily scores (last 30 days)"
  label: string;
  // Y-axis range: scores 0..100, eNPS -100..100, percentage 0..100,
  // count 0..max (variable), enps-response 0..10 (individual eNPS values).
  // Count and enps-response have no average line.
  scaleType: 'score' | 'enps' | 'percentage' | 'count' | 'enps-response';
  // Selected range, used to render the full window (e.g. all 30 days) even when
  // the server only returns periods that have data.
  dateRange: DateRange;
  // Aggregation period, derived from the server period label, used to decide
  // whether buckets are daily, weekly, or monthly.
  periodLabel?: string;
  // Value for the average reference line — the card's headline number (a
  // survey-volume-weighted grand mean from the server), so the line matches the
  // large value exactly. Omitted for count cards, which have no average line.
  averageValue?: number;
}

interface BarSlot {
  date: string;
  value: number | null; // null = period with no data (blank bar)
}

// Generate all calendar dates between start and end (inclusive).
function generateDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return dates;
}

function formatDate(dateStr: string): string {
  // Parse the YYYY-MM-DD key as a LOCAL date. `new Date('YYYY-MM-DD')` parses as
  // UTC midnight, which toLocaleDateString then renders a day earlier in any
  // timezone behind UTC (e.g. a Monday key would print as the prior Sunday).
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function BarChart({
  trendline,
  label,
  scaleType,
  dateRange,
  periodLabel,
  averageValue,
}: BarChartProps) {
  const hasData = trendline.length > 0;

  const dataMap = new Map(trendline.map((d) => [d.date, d.value]));
  const sortedDates = [...trendline].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const aggregationPeriod = getAggregationPeriod(periodLabel);

  // Render the FULL window — not just the periods the server returned — so that
  // days/weeks without data show as blank bars instead of compressing the
  // chart. The window comes from the server (same bounds it queried), so the
  // chart never recomputes "today" from the browser clock; the last-data
  // fallback only matters during the brief load before the window arrives.
  const analyticsWindow = useAnalyticsWindow();
  const lastData = hasData ? sortedDates[sortedDates.length - 1].date : '';
  const today = analyticsWindow?.end ?? lastData;
  const endDate = today;

  let slots: BarSlot[];
  if (aggregationPeriod === 'daily') {
    let startDate: string;
    if (
      dateRange === DateRange.LAST_7_DAYS ||
      dateRange === DateRange.LAST_30_DAYS
    ) {
      // Fixed ranges: render the server's window verbatim.
      startDate = analyticsWindow?.start ?? endDate;
    } else {
      // all-data daily: span the data, or a single day when there's none
      startDate = hasData ? sortedDates[0].date : endDate;
    }
    slots = generateDateRange(startDate, endDate).map((date) => ({
      date,
      value: dataMap.get(date) ?? null,
    }));
  } else {
    // Weekly/monthly (all-data): step through every bucket from the first data
    // point to the end so skipped periods render as blank bars.
    slots = [];
    if (hasData) {
      const cursor = new Date(sortedDates[0].date);
      const end = new Date(endDate);
      while (cursor <= end) {
        const key = cursor.toISOString().split('T')[0];
        slots.push({ date: key, value: dataMap.get(key) ?? null });
        if (aggregationPeriod === 'weekly') {
          cursor.setUTCDate(cursor.getUTCDate() + 7);
        } else {
          cursor.setUTCMonth(cursor.getUTCMonth() + 1);
        }
      }
    }
  }

  const values = trendline.map((d) => d.value);

  // Y-axis range: fixed for scores/percentages, variable for counts. For counts
  // round the top up to an even number so the three ticks are clean, evenly
  // spaced integers (avoids "1, 1, 0") and the tallest bar gets some headroom.
  const min = scaleType === 'enps' ? -100 : 0;
  const rawCountMax = Math.max(...values, 1);
  const countMax = rawCountMax % 2 === 0 ? rawCountMax : rawCountMax + 1;
  const max =
    scaleType === 'count' ? countMax : scaleType === 'enps-response' ? 10 : 100;
  const range = max - min || 1;

  const toPercent = (value: number) =>
    Math.max(0, Math.min(100, ((value - min) / range) * 100));

  // Average reference line for score/eNPS/percentage cards (and only with data).
  // averageValue is the card's headline number, so the line equals the large
  // value exactly. Count cards omit it and draw no line.
  const showAverage =
    scaleType !== 'count' && hasData && averageValue !== undefined;
  const averagePercent = toPercent(averageValue ?? 0);

  const axisLabels =
    scaleType === 'enps'
      ? [100, 50, 0, -50, -100]
      : scaleType === 'enps-response'
      ? [10, 5, 0]
      : scaleType === 'count'
      ? [max, max / 2, 0]
      : [100, 80, 60, 40, 20, 0];

  const firstDate = slots[0]?.date ?? endDate;
  const lastDate = slots[slots.length - 1]?.date ?? endDate;
  const lastLabel =
    lastDate === today
      ? `${formatDate(lastDate)} (Today)`
      : formatDate(lastDate);

  return (
    <TooltipProvider>
      <div className="analytics-card-barchart">
        <div className="analytics-card-barchart-label">{label}</div>
        <div className="analytics-card-barchart-body">
          <div className="analytics-card-barchart-yaxis">
            {axisLabels.map((tick) => (
              <span key={tick} className="analytics-card-barchart-tick">
                {tick}
              </span>
            ))}
          </div>
          <div className="analytics-card-barchart-plot">
            {/* Blue dashed average line (not shown for counts) */}
            {showAverage && (
              <div
                className="analytics-card-barchart-avgline"
                style={{ bottom: `${averagePercent}%` }}
              />
            )}
            <div className="analytics-card-barchart-bars">
              {slots.map((slot) => {
                // Empty period: render a non-interactive blank slot
                if (slot.value === null) {
                  return (
                    <div
                      key={slot.date}
                      className="analytics-card-barchart-slot"
                    />
                  );
                }
                // The whole column is the hover target so short bars are easy
                // to hover; the tooltip shows the exact date and value.
                return (
                  <Tooltip key={slot.date}>
                    <TooltipTrigger asChild>
                      <div className="analytics-card-barchart-slot analytics-card-barchart-slot-interactive">
                        <div
                          className="analytics-card-barchart-bar"
                          style={{ height: `${toPercent(slot.value)}%` }}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {formatDate(slot.date)}: {slot.value}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
        <div className="analytics-card-barchart-xaxis">
          <span>{formatDate(firstDate)}</span>
          <span>{lastLabel}</span>
        </div>
      </div>
    </TooltipProvider>
  );
}
